import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy'
import {BehaviorSubject, Observable, of} from 'rxjs'
import {distinctUntilChanged, first, map, mergeMap, tap} from 'rxjs/operators'
import {FormulaElement, MathSign, SignElement} from './interfaces/formula.interface'
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {CalcHttpService} from "@atl/shared/services/calc-http.service";
import {FormulaValidationEmptyLine, FormulaValidationRes} from "@atl/shared/interfaces/calc.interface";
import {ObjectsService} from "@atl/lacerta-ui-common";

export enum FORMULA_KEYS {
    ARROW_LEFT = 'ArrowLeft',
    ARROW_RIGHT = 'ArrowRight',
    BACKSPACE = 'Backspace',
    DELETE = 'Delete',
    ENTER = 'Enter',
    ARROW_DOWN = 'ArrowDown',
    ARROW_UP = 'ArrowUp',
}

type MoveDirection = 'up' | 'down' | 'left' | 'right'

const PLUS_SIGN_REPLACEMENT = '--'

@UntilDestroy()
@Component({
    selector: 'lta-formula-input',
    templateUrl: 'formula-input.component.html',
    styleUrls: ['formula-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FormulaInputComponent,
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormulaInputComponent implements OnInit, ControlValueAccessor {
    @Input() isFullScreen = false
    @ViewChild('input') input: ElementRef
    @ViewChild('area') areaRef: ElementRef<HTMLDivElement>
    @Output() onBlur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() onFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() onFullScreen: EventEmitter<void> = new EventEmitter<void>();
    public elements$ = new BehaviorSubject<FormulaElement[]>([{type: 0, selected: true}])
    public error = ''
    public isFocused: boolean
    private lastCaretPosition: number = 0
    private onChange: (formula) => {};
    private onTouched: () => {};
    private touched: Boolean;
    private valueChanged = false

    constructor(
        private objectsService: ObjectsService,
        private calcHttpService: CalcHttpService,
        private cd: ChangeDetectorRef
    ) {
    }

    private _value: string = ''

    public get value() {
        return this._value
    }

    get inputIndex() {
        return this.elements$.value.findIndex((v) => v.type === 0)
    }

    get inputElement() {
        return this.elements$.value[this.inputIndex]
    }

    get selectedIndex() {
        return this.elements$.value.findIndex((v) => v.selected)
    }

    get selectedElement() {
        return this.elements$.value[this.selectedIndex]
    }

    get previousElement() {
        return this.elements$.value[this.selectedIndex - 1]
    }

    get caretPosition() {
        let caretOffset = 0;
        let doc = this.input.nativeElement.ownerDocument || this.input.nativeElement.document;
        let win = doc.defaultView || doc.parentWindow;
        let sel;
        if (typeof win.getSelection != "undefined") {
            sel = win.getSelection();
            if (sel.rangeCount > 0) {
                let range = win.getSelection().getRangeAt(0);
                let preCaretRange = range.cloneRange();
                preCaretRange.selectNodeContents(this.input.nativeElement);
                preCaretRange.setEnd(range.endContainer, range.endOffset);
                caretOffset = preCaretRange.toString().length;
            }
        } else if ((sel = doc.selection) && sel.type != "Control") {
            let textRange = sel.createRange();
            let preCaretTextRange = doc.body.createTextRange();
            preCaretTextRange.moveToElementText(this.input.nativeElement);
            preCaretTextRange.setEndPoint("EndToEnd", textRange);
            caretOffset = preCaretTextRange.text.length;
        }
        return caretOffset;
    }

    writeValue(formula: string) {
        this._value = ''
        if (this.input) {
            this.input.nativeElement.textContent = ''
        }
        if (formula) {
            const mathSigns: string[] = Object.values(MathSign)
            const regex = new RegExp(`(${mathSigns.map(x => x.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|')})`, "g")
            const re = new RegExp(PLUS_SIGN_REPLACEMENT, "g")
            const arr = formula.replace(re, '+').replace(/ /g, '').split(regex).filter(v => v !== '')
            const elements: FormulaElement[] = arr.map(el => {
                if (mathSigns.includes(el)) {
                    return {type: 1, value: el as MathSign}
                } else {
                    return {type: 2, value: el}
                }
            })
            this.elements$.next([...elements, {type: 0, selected: true}])
        } else {
            this.elements$.next([{type: 0, selected: true}])
        }
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    public setValue(event: any) {
        this.valueChanged = true
        const {target: {textContent}, data, inputType} = event

        const isSign = this.handleSigns(data)

        const isDelete = inputType === 'deleteContentForward' || inputType === 'deleteContentBackward'

        if (isSign) {
            const index = this.caretPosition - 1
            this.input.nativeElement.innerText = this._value
            if (this._value) {
                this.setCaret(index)
            }

        } else if (!data && !isDelete) {
            const index = this.caretPosition
            this.input.nativeElement.innerText = this._value
            if (this._value) {
                this.setCaret(index)
            }
        } else {
            this._value = textContent
            const formula = this.elementToFormula(this.elements$.value)
            this.onChange(formula + textContent)
        }
    }

    @HostListener('document:click', ['$event'])
    click(event) {
        if (this.areaRef.nativeElement.contains(event.target)) {
            this.onFocus.emit()
            this.input.nativeElement.focus()
            this.isFocused = true
        } else {
            this.onBlur.emit()
            this.isFocused = false
        }
    }

    @HostListener('window:keydown', ['$event'])
    preventScroll(event: KeyboardEvent) {
        switch (event.key) {
            case FORMULA_KEYS.ARROW_DOWN:
            case FORMULA_KEYS.ARROW_UP: {
                event.preventDefault()
                break;
            }

            default:
                break;
        }
    }

    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (!this.isFocused) return
        switch (event.key) {
            case FORMULA_KEYS.ARROW_LEFT: {
                if (!this.value) {
                    this.moveSelect('left')
                } else if (this.lastCaretPosition === 0) {
                    this.markAsTouched()
                    this.addObjectElement(this.value).subscribe()
                    this.moveSelect('left')
                    this.moveSelect('left')
                }
                break;
            }

            case FORMULA_KEYS.ARROW_RIGHT: {
                if (!this.value) {
                    this.moveSelect('right')
                } else if (this.lastCaretPosition === this.value.length) {
                    this.markAsTouched()
                    this.addObjectElement(this.value).subscribe()
                }
                break;
            }

            case FORMULA_KEYS.BACKSPACE: {
                if (this.selectedIndex !== this.inputIndex || !this.valueChanged) {
                    this.deleteElement(-1)
                }

                break;
            }

            case FORMULA_KEYS.DELETE: {
                if (this.selectedIndex !== this.inputIndex || !this.valueChanged) {
                    this.deleteElement(1)
                }
                break;
            }

            case FORMULA_KEYS.ENTER: {
                if (this.value) {
                    this.markAsTouched()
                    this.addObjectElement(this.value).subscribe()
                }
                break;
            }

            case FORMULA_KEYS.ARROW_UP: {
                event.stopPropagation()
                if (!this.value) {
                    this.moveSelect('up')
                } else {
                    this.markAsTouched()
                    this.addObjectElement(this.value).subscribe()
                    this.moveSelect('up')
                }
                break;
            }

            case FORMULA_KEYS.ARROW_DOWN: {
                event.stopPropagation()
                if (!this.value) {
                    this.moveSelect('down')
                } else {
                    this.markAsTouched()
                    this.addObjectElement(this.value).subscribe()
                    this.moveSelect('down')
                }
                break;
            }

            default:
                break;
        }
        this.valueChanged = false
        this.lastCaretPosition = this.caretPosition
    }

    public addObject(id: number) {
        this.objectsService.getObjectPath(id, true)
            .pipe(first())
            .subscribe(obj => {
                let stringPath = obj.map(pathItem => pathItem.name).join('.')
                if (obj) {
                    this.addElement({type: 2, value: stringPath})
                }
            })
    }

    ngOnInit(): void {
        this.elements$
            .pipe(
                untilDestroyed(this),
                map(elements => this.elementToFormula(elements)),
                distinctUntilChanged((a, b) => a?.length === b?.length),
                tap((formula) => {
                    if (this.onChange) this.onChange(formula)
                }),
                mergeMap((formula) => {
                    if (formula) return this.calcHttpService.validateFormula(formula)
                    return of({type: 1, new_formula: '', empty_line: {}}) as Observable<FormulaValidationEmptyLine>
                })
            )
            .subscribe(res => {
                this.handleError(res)
            })
    }

    public reduceObjectValue(value: string): string {
        const words = value.split('.');
        if (words.length <= 2) return value;
        return words.slice(-2).join('.');
    }

    public addObjectElement(object: string, signObj?: SignElement) {
        if (object) {
            this.addElement({type: 2, value: object})
            this._value = ''
            if (this.input) {
                this.input.nativeElement.textContent = ''
            }        }
        return of(null)
    }

    private markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private moveInput(move: 1 | -1, elements: FormulaElement[]) {
        if (this.selectedIndex + move !== this.inputIndex) {
            this.array_move(elements, this.inputIndex, this.selectedIndex)
        }
        setTimeout(() => {
            this.input.nativeElement.focus()
        })

        return elements
    }

    private addElement(element: FormulaElement) {
        if (element.type === 1 && this.value) {
            this.markAsTouched()
            this.addObjectElement(this.value, element)
                .subscribe(
                    () => {
                        const elements = this.elements$.value
                        elements.splice(this.selectedIndex, 0, element)
                        this.elements$.next(elements)
                    }
                )
            return
        } else if (element.type === 2 && (element.value.toUpperCase() === MathSign.IF || element.value.toUpperCase() === MathSign.IF_MULTIPLE)) {
            const elements = this.elements$.value
            elements.splice(
                this.selectedIndex,
                0,
                {type: 2, value: element.value.toUpperCase()},
            )
            this.elements$.next(elements)
        } else {
            const elements = this.elements$.value
            elements.splice(this.selectedIndex, 0, element)
            this.elements$.next(elements)
        }
    }

    private deleteElement(move: 1 | -1) {
        if (this.selectedElement.type !== 0) {
            const elements = this.elements$.value
            const index = this.selectedIndex
            this.selectElement(this.inputIndex - index as any)
            elements.splice(index, 1);
            this.elements$.next(elements)
        } else if (this.selectedElement.type === 0 && !this.value) {
            this.selectElement(move === 1 ? 'right' : 'left')
        }
    }

    private selectElement(move: MoveDirection) {
        const elements = this.elements$.value
        let index: number


        if (move === 'up' || move === "down") {
            const currElIndex = this.selectedIndex
            let selectedElRect = this.areaRef.nativeElement.children[currElIndex].getBoundingClientRect();
            let leftElement: Element
            let leftIndex: number
            let rightElement: Element
            let rightIndex: number

            if (move === 'down') {
                for (let i = currElIndex + 1; i < this.areaRef.nativeElement.children.length; i++) {
                    let rect2 = this.areaRef.nativeElement.children[i].getBoundingClientRect();

                    if (selectedElRect.x < rect2.x && selectedElRect.y < rect2.y) {
                        leftIndex = i - 1
                        rightIndex = i
                        leftElement = this.areaRef.nativeElement.children[i - 1]
                        rightElement = this.areaRef.nativeElement.children[i]
                        break
                    }
                }
            } else {
                for (let i = currElIndex - 1; i >= 0; i--) {
                    let rect2 = this.areaRef.nativeElement.children[i].getBoundingClientRect();

                    if (selectedElRect.x > rect2.x && selectedElRect.y > rect2.y) {
                        leftIndex = i
                        rightIndex = i + 1
                        leftElement = this.areaRef.nativeElement.children[i]
                        rightElement = this.areaRef.nativeElement.children[i + 1]
                        break
                    }
                }
            }
            if (move === "down" && !leftElement && !rightElement) {
                index = elements.length - 1
            } else if (move === "up" && !leftElement && !rightElement) {
                index = 0
            } else {
                const selectedCenter = selectedElRect.x + selectedElRect.width / 2
                const leftCenter = leftElement.getBoundingClientRect().x + leftElement.getBoundingClientRect().width / 2
                const rightCenter = rightElement.getBoundingClientRect().x + rightElement.getBoundingClientRect().width / 2
                const leftDiff = selectedCenter - leftCenter
                const rightDiff = rightCenter - selectedCenter

                if (leftDiff < rightDiff) {
                    index = leftIndex
                } else {
                    index = rightIndex
                }
            }

        } else {
            index = this.selectedIndex + (move === 'left' ? -1 : 1)
        }


        const atBeginning = this.selectedIndex === 0
        const atEnd = this.selectedIndex === elements.length - 1
        if ((atBeginning && move === 'left') || (atEnd && move === 'right')) return
        elements.forEach((el, i) => i === index ? el.selected = true : el.selected = false)
        if (elements[index].type === 0) {
            this.inputElement.hidden = false
            setTimeout(() => {
                this.input.nativeElement.focus()
            })
        } else {
            this.inputElement.hidden = true
        }
    }

    private handleSigns(key: string): boolean {
        switch (key) {
            case MathSign.PLUS: {
                this.addElement({type: 1, value: MathSign.PLUS})
                return true;
            }

            case MathSign.MINUS: {
                this.addElement({type: 1, value: MathSign.MINUS})
                return true;
            }

            case MathSign.MULTIPLY: {
                this.addElement({type: 1, value: MathSign.MULTIPLY})
                return true;
            }

            case MathSign.DIVIDE: {
                this.addElement({type: 1, value: MathSign.DIVIDE})
                return true;
            }

            case MathSign.LEFT_PARENTHESIS: {
                this.addElement({type: 1, value: MathSign.LEFT_PARENTHESIS})
                return true;
            }

            case MathSign.RIGHT_PARENTHESIS: {
                this.addElement({type: 1, value: MathSign.RIGHT_PARENTHESIS})
                return true;
            }

            case MathSign.GREATER_THEN: {
                this.addElement({type: 1, value: MathSign.GREATER_THEN})
                return true;
            }

            case MathSign.LESS_THEN: {
                this.addElement({type: 1, value: MathSign.LESS_THEN})
                return true;
            }

            case MathSign.ARGUMENT_DIVIDER: {
                this.addElement({type: 1, value: MathSign.ARGUMENT_DIVIDER})
                return true;
            }

            case MathSign.EQUAL: {
                if (this.previousElement && this.previousElement.type === 1) {
                    switch (this.previousElement.value) {
                        case MathSign.GREATER_THEN: {
                            this.previousElement.value = MathSign.MORE_OR_EQUAL
                            this.elements$.next(this.elements$.value)
                            return true
                        }
                        case MathSign.LESS_THEN: {
                            this.previousElement.value = MathSign.LESS_OR_EQUAL
                            this.elements$.next(this.elements$.value)
                            return true
                        }
                        case MathSign.NOT: {
                            this.previousElement.value = MathSign.NOT_EQUAL
                            this.elements$.next(this.elements$.value)
                            return true
                        }
                    }
                }
                this.addElement({type: 1, value: MathSign.EQUAL})
                return true;
            }

            case MathSign.POWER: {
                this.addElement({type: 1, value: MathSign.POWER})
                return true;
            }

            case MathSign.NOT: {
                this.addElement({type: 1, value: MathSign.NOT})
                return true;
            }
            default:
                return false;
        }
    }

    private setCaret(index: number) {
        var el = this.input.nativeElement
        var range = document.createRange()
        var sel = window.getSelection()

        range.setStart(el.childNodes[0], index)
        range.collapse(true)

        sel.removeAllRanges()
        sel.addRange(range)
    }

    private getError(type: number) {
        switch (type) {
            case 0:
                return null

            case 1:
                return null
            // return 'lta-errors.emptyLine'

            case 2:
                return 'lta-errors.incorrectLine'

            case 3:
                return 'lta-errors.unknownObject'

            case 4:
                return 'lta-errors.unmatchedBrackets'

            default:
                return null
        }
    }

    private handleError(res: FormulaValidationRes) {
        switch (res.type) {
            case 0:
                this.elements$.value.forEach(el => el.error = false)
                break;

            case 1:
                break;

            case 2:
                break;

            case 3:
                this.elements$.value.forEach(el => {
                    if (el.type !== 0 && res.unknown_object.obj_name.includes(el.value)) {
                        el.error = true
                    }
                })
                break;

            case 4:
                break;

            default:
                console.log('Unhandled error: ', res);
                break
        }

        this.error = this.getError(res.type)
        this.cd.markForCheck()
    }

    private moveSelect(move: MoveDirection) {
        let elements = this.elements$.value
        if (this.selectedElement.type === 0) {
            this.selectElement(move)
        } else {
            let isInputMoved = false
            if (move !== "down" && move !== "up") {
                elements = this.moveInput(move === 'right' ? 1 : -1, elements)
                isInputMoved = true
            }
            this.selectElement(move)

            // Костыль для фикса бага при движении вверх или вниз и последующем движении вправо
            if (isInputMoved && this.selectedIndex !== this.inputIndex) {
                this.array_move(elements, this.inputIndex, this.selectedIndex - 1)
                elements.forEach((el, i) => i === this.inputIndex ? el.selected = true : el.selected = false)
            }
        }

        this.elements$.next(elements)
    }

    private array_move(arr, old_index, new_index) {
        while (old_index < 0) {
            old_index += arr.length;
        }
        while (new_index < 0) {
            new_index += arr.length;
        }
        if (new_index >= arr.length) {
            let k = new_index - arr.length + 1;
            while (k--) {
                arr.push(undefined);
            }
        }
        arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    };

    private elementToFormula(elements: FormulaElement[]) {
        return elements.reduce((pr, c) => {
            if (c.type === 0) {
                return pr
            } else if (c.type === 1) {
                const sign = c.value.replace('+', PLUS_SIGN_REPLACEMENT)
                return pr + ` ${sign} `
            } else {
                return pr + c.value
            }
        }, '')
    }
}
